import { useState, useEffect, useMemo } from "react"
import { Routes, Route, useLocation } from "react-router-dom"
import { ThemeProvider } from "@mui/material/styles"
import { ThemeProvider as LegacyThemeProvider } from "@mui/styles"
import CssBaseline from "@mui/material/CssBaseline"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import Sidenav from "examples/Sidenav"
import Configurator from "examples/Configurator"
import theme from "assets/theme"
import themeDark from "assets/theme-dark"
import rtlPlugin from "stylis-plugin-rtl"
import createCache from "@emotion/cache"
import { commonRoutes } from "routes"
import { getRoleMenu, getSwitchableRoles } from "menus/registry"
import { getAccessibleRoutes, getAccessibleRoutePaths } from "shared/procurement/accessibleRoutes"
import { AccessibleRoutesProvider } from "shared/procurement/AccessibleRoutesContext"
import useIsDepartmentManager from "shared/procurement/useIsDepartmentManager"
import { AuthWrapper, FallbackRedirect } from "shared/router/authWrapper"
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context"
import brandWhite from "assets/images/logo-ct.png"
import brandDark from "assets/images/logo-ct-dark.png"
import { useSelector, useDispatch } from "react-redux"
import {
  selectAuth,
  setIsManager,
  setActiveRoleByCode,
} from "shared/redux/slices/authSlice"
import { generateToken } from "shared/libs/firebase"
import { onMessage } from "firebase/messaging"
import { messaging } from "shared/libs/firebase"
import { useNotificationSubscriptionMutation } from "shared/redux/endpoints/notifications"
import { useNotification } from "examples/Header/hooks/useNotification"
import { detect } from "detect-browser"
import { useMediaQuery } from "@mui/material"
// import { useBanners } from "pages/home/hooks/useBanners";
import { useLazyGetBannerForTeacherQuery } from "shared/redux/endpoints/banner"
import { useLazyCheckManagerQuery } from "shared/redux/endpoints/auth"
import AntiDebug from "components/AntiDebug/AntiDebug"
import AuthenticationToken from "pages/AuthenticationToken"
const browser = detect()

export default function App() {
  const [controller, dispatch] = useMaterialUIController()
  const reduxDispatch = useDispatch()
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller
  const [onMouseEnter, setOnMouseEnter] = useState(false)
  const [rtlCache, setRtlCache] = useState(null)
  const { pathname } = useLocation()
  const auth = useSelector(selectAuth)
  const { accessToken, refreshToken } = auth
  const [triggerCheckManager] = useLazyCheckManagerQuery()
  const [triggerBanners] = useLazyGetBannerForTeacherQuery()
  const location = useLocation()

  const isAuthenticated = useMemo(
    () => !!accessToken && !!refreshToken,
    [accessToken, refreshToken]
  )

  const isDepartmentManager = useIsDepartmentManager()
  const currentRoutesOnRole = useMemo(
    () => getAccessibleRoutes(auth, { isDepartmentManager }),
    [auth?.role, auth?.user?.isManager, auth?.roles, isDepartmentManager]
  )
  const accessibleRoutePaths = useMemo(
    () => getAccessibleRoutePaths(auth, { isDepartmentManager }),
    [auth?.role, auth?.user?.isManager, auth?.roles, isDepartmentManager]
  )

  useEffect(() => {
    const syncManagerFlag = async () => {
      const centerId = auth?.centerId

      if (!getRoleMenu(auth?.role)?.needsManagerCheck) return
      if (!centerId) {
        reduxDispatch(setIsManager({ isManager: false }))
        return
      }
      const managerResult = await triggerCheckManager({ centerId })
      const isManager = !!(
        managerResult?.data?.data ?? managerResult?.data?.status
      )
      reduxDispatch(setIsManager({ isManager }))
    }

    syncManagerFlag()
  }, [auth?.role, auth?.centerId, reduxDispatch, triggerCheckManager])

  // Guard a persisted active role: if the stored role isn't backed by the
  // registry (e.g. after re-login with a different role set), fall back to the
  // user's first switchable role so the menu/routes stay consistent.
  useEffect(() => {
    if (!isAuthenticated) return
    if (getRoleMenu(auth?.role)) return
    const switchable = getSwitchableRoles(auth?.roles)
    if (switchable.length > 0) {
      reduxDispatch(setActiveRoleByCode({ code: switchable[0] }))
    }
  }, [isAuthenticated, auth?.role, auth?.roles, reduxDispatch])

  const defaultRoute = useMemo(
    () => getRoleMenu(auth?.role)?.defaultRoute || "/",
    [auth?.role]
  )

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    })

    setRtlCache(cacheRtl)
  }, [])

  // const hideSidenavMatch = useMediaQuery("max-width: 1200px");

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    // if (miniSidenav && !onMouseEnter) {
    //   setMiniSidenav(dispatch, false);
    //   setOnMouseEnter(true);
    // }
  }

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true)
      setOnMouseEnter(false)
    }
  }

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator)

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction)
  }, [direction])

  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.scrollingElement.scrollTop = 0
  }, [pathname])

  useEffect(() => {
    const metaName = "user-email"
    const email = auth?.user?.email
    let meta = document.querySelector(`meta[name="${metaName}"]`)

    if (isAuthenticated && email) {
      if (!meta) {
        meta = document.createElement("meta")
        meta.setAttribute("name", metaName)
        document.head.appendChild(meta)
      }
      meta.setAttribute("content", email)
      return
    }

    if (meta) {
      meta.remove()
    }
  }, [isAuthenticated, auth?.user?.email])

  useEffect(() => {
    const setAATriggerVisibility = (visible) => {
      const el = document.getElementById("aa-trigger")
      if (el) {
        el.style.display = visible ? "" : "none"
        return true
      }
      return false
    }

    if (!setAATriggerVisibility(isAuthenticated)) {
      // widget chưa inject vào DOM, dùng MutationObserver để đợi
      const observer = new MutationObserver(() => {
        if (setAATriggerVisibility(isAuthenticated)) observer.disconnect()
      })
      observer.observe(document.body, { childList: true, subtree: true })
      return () => observer.disconnect()
    }
  }, [pathname, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      window.DJANGO_USER_EMAIL = auth?.user?.email
    }
  }, [isAuthenticated])

  const [subscribe] = useNotificationSubscriptionMutation()

  const { fetchAll } = useNotification()

  useEffect(() => {
    if (isAuthenticated) {
      triggerBanners({
        size: 10,
        page: 0,
      })
    }
  }, [isAuthenticated])

  // Guided tour disabled.
  // useEffect(() => {
  //   if (
  //     isAuthenticated &&
  //     (auth?.role === RoleLabel[Role.LXO] || auth?.role === RoleLabel[Role.LX])
  //   ) {
  //     setTimeout(() => {
  //       if (window.startTour) {
  //         window.startTour()
  //       }
  //     }, 500)
  //   }
  // }, [location.pathname, isAuthenticated])

  useEffect(() => {
    const initNotification = async () => {
      const token = await generateToken()

      await subscribe({
        token,
        device: "WEB",
        browser: browser
          ? browser.name === "edge-chromium"
            ? "EDGE"
            : browser.name.toUpperCase()
          : "CHROME",
      })
      onMessage(messaging, (payload) => {
        const data = payload.data.body
        const parsedData = JSON.parse(data)
        fetchAll(parsedData)
      })
    }
    if (isAuthenticated) {
      initNotification()
    }
  }, [isAuthenticated])

  const getRoutes = (allRoutes) => {
    return allRoutes.map((route) => {
      if (route.collapse) {
        return (
          <>
            <Route
              exact
              path={route.route}
              element={
                route.isPrivate ? (
                  <AuthWrapper
                    isAllowed={isAuthenticated}
                    redirectPath={route.redirectPath}
                  >
                    {route.component}
                  </AuthWrapper>
                ) : (
                  route.component
                )
              }
              key={route.key}
            />
            {getRoutes(route.collapse)}
          </>
        )
      }

      if (route.route && route.component) {
        return (
          <Route
            exact
            path={route.route}
            element={
              route.isPrivate ? (
                <AuthWrapper
                  isAllowed={isAuthenticated}
                  redirectPath={route.redirectPath}
                >
                  {route.component}
                </AuthWrapper>
              ) : (
                route.component
              )
            }
            key={route.key}
          />
        )
      }

      return null
    })
  }

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={() => {
        handleConfiguratorOpen()
      }}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  )
  const appTheme = darkMode ? themeDark : theme
  return (
    <AccessibleRoutesProvider paths={accessibleRoutePaths}>
      <ThemeProvider theme={appTheme}>
        {isAuthenticated && auth?.user?.email && (
          <div
            id="user-email"
            data-user-email={auth.user.email}
            style={{ display: "none" }}
          />
        )}
        <CssBaseline />
        {/* <AntiDebug /> */}
        {isAuthenticated && layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName="Material Dashboard PRO"
              routes={currentRoutesOnRole}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
          </>
        )}
        <Routes>
          {getRoutes(currentRoutesOnRole)}
          {getRoutes(commonRoutes)}
          <Route path="/redirect-token" element={<AuthenticationToken />} />
          <Route path="*" element={<FallbackRedirect isAuthenticated={isAuthenticated} defaultRoute={defaultRoute} />} />
        </Routes>
      </ThemeProvider>
    </AccessibleRoutesProvider>
  )
}
